/**
 * Location Utilities
 * Shared functions for ZIP/location handling across the theme
 * Consolidates duplicate code from store-picker.js, product-delivery-checker.js,
 * product-location-filter.js, and store-locator.js
 */
window.LocationUtils = (function() {
  const LOCATION_KEY = 'soil3_location';
  const STORE_KEY = 'soil3_store';

  return {
    // ============ CONSTANTS ============
    LOCATION_KEY: LOCATION_KEY,
    STORE_KEY: STORE_KEY,

    // ============ VALIDATION ============
    isValidZip(zip) {
      return /^\d{5}$/.test(zip);
    },

    // ============ ZIP PARSING ============
    parseZipRanges(zipString) {
      if (!zipString) return [];
      const zips = [];
      const parts = zipString.split(',').map(s => s.trim());

      parts.forEach(part => {
        if (part === 'default' || part === 'all') return;

        if (part.includes('-')) {
          const [start, end] = part.split('-').map(z => parseInt(z.trim()));
          if (!isNaN(start) && !isNaN(end)) {
            for (let i = start; i <= end; i++) {
              zips.push(i.toString().padStart(5, '0'));
            }
          }
        } else if (/^\d{5}$/.test(part)) {
          zips.push(part);
        }
      });

      return zips;
    },

    isZipInRange(zipString, userZip) {
      if (!zipString || !userZip) return false;
      return this.parseZipRanges(zipString).includes(userZip);
    },

    // ============ DISTANCE ============
    calculateDistance(lat1, lon1, lat2, lon2) {
      if ([lat1, lon1, lat2, lon2].some(v => typeof v !== 'number' || isNaN(v))) {
        return Infinity;
      }
      const R = 3958.8; // Earth radius in miles
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    },

    // ============ STORAGE: LOCATION ============
    getLocation() {
      try {
        const data = localStorage.getItem(LOCATION_KEY);
        return data ? JSON.parse(data) : null;
      } catch (e) {
        return null;
      }
    },

    setLocation(zipCode, source = 'zip', coordinates = null) {
      const data = {
        zipCode,
        source,
        coordinates,
        timestamp: Date.now()
      };
      localStorage.setItem(LOCATION_KEY, JSON.stringify(data));

      // Dispatch event
      document.dispatchEvent(new CustomEvent('location:changed', { detail: data }));

      return data;
    },

    getZipCode() {
      const location = this.getLocation();
      return location?.zipCode || null;
    },

    clearLocation() {
      localStorage.removeItem(LOCATION_KEY);
      document.dispatchEvent(new CustomEvent('location:changed', { detail: null }));
    },

    // ============ STORAGE: STORE ============
    getStore() {
      try {
        const data = localStorage.getItem(STORE_KEY);
        return data ? JSON.parse(data) : null;
      } catch (e) {
        return null;
      }
    },

    setStore(id, name, zip) {
      console.log('[6] LocationUtils.setStore called with:', id, name, zip);
      const data = { id, name, zip };
      localStorage.setItem(STORE_KEY, JSON.stringify(data));

      // Dispatch event
      console.log('[6] Dispatching store:selected event with:', data);
      document.dispatchEvent(new CustomEvent('store:selected', { detail: data }));

      return data;
    },

    clearStore() {
      localStorage.removeItem(STORE_KEY);
    },

    // ============ MIGRATION ============
    // One-time migration from old keys to new format
    migrateFromLegacy() {
      // Always clean up old keys (prevents stale data issues)
      const oldKeys = ['userSearchLocation', 'userSearchLocationType', 'userSearchCoordinates',
       'userStoreId', 'userStoreName', 'userLocation', 'selectedStoreZip', 'userStoreZip'];

      // Check if already migrated - just clean up and return
      if (localStorage.getItem(LOCATION_KEY)) {
        oldKeys.forEach(key => localStorage.removeItem(key));
        return;
      }

      // Try to get ZIP from old keys before we delete them
      let zipCode = null;
      for (const key of ['userSearchLocation', 'userLocation', 'selectedStoreZip']) {
        const value = localStorage.getItem(key);
        if (value && /^\d{5}$/.test(value)) {
          zipCode = value;
          break;
        }
      }

      // Migrate location
      if (zipCode) {
        const source = localStorage.getItem('userSearchLocationType') || 'zip';
        let coordinates = null;
        try {
          coordinates = JSON.parse(localStorage.getItem('userSearchCoordinates'));
        } catch (e) {}

        // Set without dispatching event during migration
        const data = {
          zipCode,
          source,
          coordinates,
          timestamp: Date.now()
        };
        localStorage.setItem(LOCATION_KEY, JSON.stringify(data));
      }

      // Migrate store
      const storeId = localStorage.getItem('userStoreId');
      const storeName = localStorage.getItem('userStoreName');
      if (storeId && storeName) {
        const storeData = { id: storeId, name: storeName, zip: zipCode };
        localStorage.setItem(STORE_KEY, JSON.stringify(storeData));
      }

      // Clean up old keys
      oldKeys.forEach(key => localStorage.removeItem(key));
    }
  };
})();

// Run migration on load
document.addEventListener('DOMContentLoaded', () => {
  LocationUtils.migrateFromLegacy();
});
